import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { CUSTOM_MASK } from '@helpers/custom-validator';
import { INPUT_TYPES_MAP } from '@constants/input-types-map';
import { CodeRepository } from '@data-layer/repositories';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { take, takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { CANADA_COUNTRY_CODE, MEXICO_COUNTRY_CODE, USA_COUNTRY_CODE } from '@constants/constants';
import { CountryModel } from '@data-layer/models/common';
import { EntityModel } from '@data-layer/models/entities';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';

@Component({
  selector: 'address-view-isf',
  templateUrl: './address-view-isf.component.html',
  styleUrls: ['./address-view-isf.component.scss'],
})
export class AddressViewIsfComponent implements OnInit, OnDestroy {
  @Input() public formGroup: UntypedFormGroup;
  @Input() public code: enumEntityModelCode;
  @Input() public errors: { [name: string]: IErrorForm } = {};
  @Input() public isShowState: boolean = true;
  @Input() public isDesktop = false;
  @Input() public isShowCountry: boolean = false;
  @Input() public isFilterRestrictedCountries: boolean = false;
  @Input() public defaultCountry: any = null;
  @Input() public isFilterOnlyUSA: boolean;

  public addressFormGroup: UntypedFormGroup;
  public CUSTOM_MASK: any = CUSTOM_MASK;
  public postalCodeMask: string;
  public postalCodeMaskTooltip: string;
  public postalCodePattern: string;
  public integerInputMap: { mask: string; pattern: string } = INPUT_TYPES_MAP.INTEGER;
  public buyerSide: enumEntityModelCode[] = [
    enumEntityModelCode.IMPORTER,
    enumEntityModelCode.BUYER,
    enumEntityModelCode.SHIP_TO,
    enumEntityModelCode.CONSIGNEE,
  ];
  public sellerSide: enumEntityModelCode[] = [
    enumEntityModelCode.SELLER,
    enumEntityModelCode.MANUFACTURER,
    enumEntityModelCode.CONSOLIDATOR,
    enumEntityModelCode.STAFFING_LOCATION,
  ];
  public isForeignCompany: boolean = false;
  public company: CompanyModel;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private codeRepository: CodeRepository, private cdr: ChangeDetectorRef, private companiesService: CompaniesService) {}

  public ngOnInit(): void {
    this.company = this.companiesService.getCompany();

    this.addressFormGroup = this.formGroup.get('address') as UntypedFormGroup;
    this.setStateForm(this.addressFormGroup.get('country').value);

    if (
      (this.buyerSide.includes(this.code) && this.defaultCountry) ||
      (this.code === enumEntityModelCode.ABI_CLIENT && !this.formGroup.get(['address', 'country']).value)
    ) {
      this.setDefaultCountry();
    }
    this.updateMask();

    this.formGroup?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val: EntityModel) => {
      this.isForeignCompany = val?.entityIdentifierQualifier === enumIdentifierQualifier.ANI;
      this.updateMask();
    });

    this.addressFormGroup
      .get('country')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val: CountryModel) => {
        if (val?.code !== this.addressFormGroup.get('state').value?.countryCode) {
          this.addressFormGroup.get('state').reset();
        }

        this.setStateForm(val);
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public get isSellerSide(): boolean {
    return this.code && this.sellerSide.includes(this.code);
  }

  private setDefaultCountry() {
    this.codeRepository
      .getCountry(this.defaultCountry || 'US')
      .pipe(take(1))
      .subscribe((val: CountryModel) => {
        this.formGroup.get(['address', 'country']).patchValue(val);
        this.setStateForm(val);

        this.cdr.markForCheck();
      });
  }

  private updateMask() {
    if (this.sellerSide.includes(this.code) || this.isForeignCompany || this.isForeignImporter) {
      this.postalCodeMask = 'F{9}';
      this.postalCodeMaskTooltip = 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL';
      this.postalCodePattern = null;
    } else {
      this.postalCodeMask = '0{9}';
      this.postalCodePattern = INPUT_TYPES_MAP.INTEGER.pattern;
      this.postalCodeMaskTooltip = INPUT_TYPES_MAP.INTEGER.maskErrorTooltip;
    }
  }

  private setStateForm(val: CountryModel): void {
    // Don't change state's enabled status if country not set yet
    if (!val || !val.code) {
      return;
    }

    if ([CANADA_COUNTRY_CODE, USA_COUNTRY_CODE, MEXICO_COUNTRY_CODE].includes(val.code)) {
      this.addressFormGroup.get('state').enable();
    } else {
      this.addressFormGroup.get('state').disable();
    }
  }

  public get isForeignImporter(): boolean {
    return (
      this.company?.identifierQualifier === enumIdentifierQualifier.ANI &&
      [enumEntityModelCode.IMPORTER, enumEntityModelCode.BUYER, enumEntityModelCode.SHIP_TO].includes(this.code)
    );
  }
}
